import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import type { ApiError } from '@/services'
import { cn } from '@/lib/utils'

/**
 * The shared error state (PX-W6.2) — the mapped backend message plus the
 * optional retry affordance. A 403 is NOT this panel's business: surfaces
 * render `ForbiddenPanel` for that (AC-7 precedence).
 *
 * PX-W6.6 stamps `data-triad="error"` on the root (see `LoadingPanel`).
 */
export function ErrorPanel({
  error,
  onRetryClicked,
  className
}: {
  error: ApiError
  onRetryClicked?: () => void
  className?: string
}) {
  const { t } = useTranslation('common')
  const toMessage = useApiErrorMessage()
  const message = toMessage(error)

  return (
    <div
      role="alert"
      data-triad="error"
      className={cn('rounded-lg border border-border bg-card px-4 py-5', className)}
    >
      <SectionLabel>{t('states.errorTitle')}</SectionLabel>
      {message !== null && <p className="mt-1.5 text-sm text-foreground">{message}</p>}
      {(error.status > 0 || error.backendKey !== undefined) && (
        <dl className="mt-1.5 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
          {error.status > 0 && (
            <div className="flex items-center gap-1">
              <dt>{t('states.statusLabel')}</dt>
              <dd data-testid="error-panel-status" className="v-figure tabular-nums text-foreground">
                {error.status}
              </dd>
            </div>
          )}
          {error.backendKey !== undefined && (
            <div className="flex items-center gap-1">
              <dt>{t('states.codeLabel')}</dt>
              <dd data-testid="error-panel-code" className="font-mono text-foreground">
                {error.backendKey}
              </dd>
            </div>
          )}
        </dl>
      )}
      {onRetryClicked !== undefined && (
        <button
          type="button"
          onClick={onRetryClicked}
          className="mt-3 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('states.retry')}
        </button>
      )}
    </div>
  )
}
